// Real BCM photography only.
//
// Each entry below is a slot for a photograph that already exists in the BCM
// Google Drive album "2026–27 BCM Photos". Drop the hosted file URL into `src`
// and the frame renders the real photo; until then the frame renders a labeled
// placeholder so no fabricated or AI-generated people are ever shown.
//
// Never label a photo with an event the source folder does not verify.

export type AlbumName = 'Summer ’26' | 'Fall ’26' | 'Spring ’27'

export type BcmPhoto = {
  id: string
  /** Album folder in the BCM Drive archive. */
  album: AlbumName
  /** Verified subject/event from the source folder — also the frame label. */
  subject: string
  /** Meaningful alt text for the real image. */
  alt: string
  /** Caption shown in the gallery. Only set when the event is known. */
  caption?: string
  /** Hosted URL of the real BCM photograph. Leave empty until supplied. */
  src?: string
}

export const albumMeta: {
  name: AlbumName
  hasPhotos: boolean
  status: string
  /** Public-facing message shown when the folder has no photos yet. */
  message?: string
  note: string
}[] = [
  {
    name: 'Summer ’26',
    hasPhotos: true,
    status: 'Photos available',
    note: 'Pickleball Night and Tabling photography.',
  },
  {
    name: 'Fall ’26',
    hasPhotos: false,
    status: 'Coming soon',
    message: 'Fall photos will be added after the first worship night on August 27.',
    note: 'Folder created — no photos yet.',
  },
  {
    name: 'Spring ’27',
    hasPhotos: false,
    status: 'Coming soon',
    message: 'Spring photos will be added as the semester begins.',
    note: 'Folder created — no photos yet.',
  },
]

/** Kept for the homepage album summary line. */
export const photoAlbums = albumMeta.map((album) => ({
  name: album.name,
  status: album.hasPhotos ? ('Photos available' as const) : ('No photos yet' as const),
  note: album.note,
}))

/**
 * Every photo slot in the archive, grouped by verified subject.
 * Add more slots as more real photos are exported from the Drive album.
 */
export const libraryPhotos: BcmPhoto[] = [
  {
    id: 'summer26-pickleball-1',
    album: 'Summer ’26',
    subject: 'Pickleball Night',
    alt: 'BCM students playing together at Pickleball Night, Summer 2026.',
    caption: 'Pickleball Night · Summer ’26',
  },
  {
    id: 'summer26-tabling-1',
    album: 'Summer ’26',
    subject: 'Tabling',
    alt: 'BCM students tabling on the UCF campus, Summer 2026.',
    caption: 'Tabling on campus · Summer ’26',
  },
  {
    id: 'summer26-pickleball-2',
    album: 'Summer ’26',
    subject: 'Pickleball Night',
    alt: 'Students talking and laughing between games at BCM Pickleball Night.',
    caption: 'Pickleball Night · Summer ’26',
  },
  {
    id: 'summer26-tabling-2',
    album: 'Summer ’26',
    subject: 'Tabling',
    alt: 'A BCM student meeting new students at the tabling table on campus.',
    caption: 'Tabling on campus · Summer ’26',
  },
  {
    id: 'summer26-pickleball-3',
    album: 'Summer ’26',
    subject: 'Pickleball Night',
    alt: 'Students waiting to rotate in at BCM Pickleball Night.',
    caption: 'Pickleball Night · Summer ’26',
  },
  {
    id: 'summer26-pickleball-4',
    album: 'Summer ’26',
    subject: 'Pickleball Night',
    alt: 'BCM students gathered together after Pickleball Night.',
    caption: 'Pickleball Night · Summer ’26',
  },
  {
    id: 'summer26-tabling-3',
    album: 'Summer ’26',
    subject: 'Tabling',
    alt: 'BCM students handing out information about Thursday worship nights.',
    caption: 'Tabling on campus · Summer ’26',
  },
]

export const featuredPhoto: BcmPhoto = libraryPhotos[0]

export const heroPhotos: BcmPhoto[] = [
  libraryPhotos[0],
  libraryPhotos[1],
  libraryPhotos[2],
]

export const galleryPhotos: BcmPhoto[] = libraryPhotos.slice(0, 5)

export const videoPosterPhoto: BcmPhoto = {
  id: 'video-poster',
  album: 'Summer ’26',
  subject: 'Pickleball Night',
  alt: 'BCM students together at Pickleball Night, used as the welcome video poster image.',
}
